import { FIRST_PAGE } from 'data/global';
import { useCallback, useEffect, useRef, useState } from 'react';
import useTimer from './useTimer';
import { PageChangeEvent } from '@react-pdf-viewer/core';

const IDLE_TIME_PERIOD = 60; // in seconds

const addListeners = (callback: () => void) => {
  document.addEventListener('wheel', callback);
  document.addEventListener('keydown', callback);
  document.addEventListener('mousemove', callback);
};

const removeListeners = (callback: () => void) => {
  document.removeEventListener('wheel', callback);
  document.removeEventListener('keydown', callback);
  document.removeEventListener('mousemove', callback);
};

const useFilePreviewTrackTime = ({
  isModalOpen,
  trackingKey,
  sendReport,
  onDocumentViewed,
}: {
  isModalOpen: boolean;
  // identifies the file currently being previewed. A change (e.g. the user
  // navigates to the next/previous file) ends the current viewing session and
  // starts a new one.
  trackingKey: string;
  sendReport: (time: number, currentPage: number) => void;
  onDocumentViewed: (time: number) => void;
}) => {
  const [isCounting, setIsCounting] = useState(false);
  const [isInIdle, setIsInIdle] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false); //file is loaded
  const [currentPage, setCurrentPage] = useState(+FIRST_PAGE);

  const totalTimeRef = useRef(0);

  // mirror the latest values so the end-of-session flush (which runs detached,
  // after the file/modal has already changed) can read them without depending on
  // them and re-running every tick.
  const timeRef = useRef(0);
  const currentPageRef = useRef(+FIRST_PAGE);
  const loadedRef = useRef(false);

  const { time, idleTime, startTimer, endTimer, resetIdleTimer } = useTimer();

  const onDocumentLoad = useCallback(() => setIsLoaded(true), []);

  // report the time spent on a single segment (a page of the current file) and
  // accumulate it into the file's total.
  const reportSegment = useCallback(
    (segmentTime: number, page: number) => {
      if (!segmentTime) return;

      sendReport(segmentTime, page);
      totalTimeRef.current += segmentTime;
    },
    [sendReport],
  );

  const start = useCallback(() => {
    if (!isLoaded) return;

    setIsCounting(true);
    setIsInIdle(false);

    addListeners(resetIdleTimer);

    startTimer();
  }, [isLoaded, resetIdleTimer, startTimer]);

  const end = useCallback(() => {
    if (!isLoaded) return;

    setIsCounting(false);

    removeListeners(resetIdleTimer);

    endTimer();
  }, [isLoaded, endTimer, resetIdleTimer]);

  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    loadedRef.current = isLoaded;
  }, [isLoaded]);

  // A "viewing session" is one file shown while the modal is open. It ends when
  // the modal closes OR another file is opened; both flush the report for the
  // file being left and reset tracking for the next one. The `reportSegment` /
  // `onDocumentViewed` captured by the cleanup are the ones bound to the file the
  // session started with, so the time is always attributed to the right document.
  const sessionKey = isModalOpen ? trackingKey : null;

  useEffect(() => {
    if (sessionKey === null) return;

    return () => {
      if (loadedRef.current) {
        reportSegment(timeRef.current, currentPageRef.current);
        onDocumentViewed(totalTimeRef.current);
      }

      totalTimeRef.current = 0;
      timeRef.current = 0;
      currentPageRef.current = +FIRST_PAGE;

      setCurrentPage(+FIRST_PAGE);
      setIsCounting(false);
      setIsInIdle(false);
      setIsLoaded(false);

      endTimer();
      removeListeners(resetIdleTimer);
    };
  }, [sessionKey, reportSegment, onDocumentViewed, endTimer, resetIdleTimer]);

  const onPageChange = useCallback(
    (event: PageChangeEvent) => {
      if (!isLoaded) return;

      // event counter for currentPage starts with 0
      const page = event.currentPage + 1;
      if (currentPage === page) return;

      reportSegment(time, currentPage);

      endTimer();
      startTimer();
      setCurrentPage(page);
    },
    [isLoaded, currentPage, time, endTimer, startTimer, reportSegment],
  );

  // if in idle, wait for next action to start timer again
  useEffect(() => {
    if (!isLoaded) return;

    if (isInIdle) addListeners(start);
    else removeListeners(start);
  }, [isInIdle, isLoaded, start]);

  // start counting once a file is shown, then pause after a period of inactivity
  useEffect(() => {
    if (!isLoaded || !isModalOpen) return;

    if (!isCounting && !isInIdle) start();

    if (idleTime >= IDLE_TIME_PERIOD) {
      reportSegment(time, currentPage);
      end();
      setIsInIdle(true);
    }
  }, [
    idleTime,
    isCounting,
    isInIdle,
    time,
    isLoaded,
    currentPage,
    isModalOpen,
    end,
    start,
    reportSegment,
  ]);

  return {
    isLoaded,
    onDocumentLoad,
    onPageChange,
  };
};

export default useFilePreviewTrackTime;
